import type { ReactNode } from 'react';
import { cn } from '../../lib/cn.ts';
import { Tooltip } from '../Tooltip.tsx';
import { DATA_SLOT_BG } from './PulseChart.tsx';
import type { DataSlot } from './PulseChart.tsx';

/** The smallest "nice" number (1, 2, 2.5, 5 times a power of ten) at or above v. */
export function niceCeil(v: number): number {
  if (!(v > 0)) return 1;
  const p = 10 ** Math.floor(Math.log10(v));
  for (const m of [1, 2, 2.5, 5, 10]) if (m * p >= v) return m * p;
  return 10 * p;
}

export interface ColumnChartProps {
  /** Oldest first, one column each. */
  values: readonly number[];
  /** Columns before this index are context (an earlier period): drawn grey. */
  contextBefore?: number;
  slot?: DataSlot;
  /** The tooltip heading for column i: "Oct 4". */
  columnLabel: (i: number) => string;
  /** A line under the heading in the tooltip, after the value. */
  columnNote?: (i: number) => ReactNode;
  format: (n: number) => string;
  /** Short tick labels on the value axis: "$40". */
  axisFormat?: (n: number) => string;
  /** Labels under chosen columns, by index ("Sep 19", "Today"). */
  xLabels?: readonly { index: number; label: string }[];
  /** A dashed threshold with its label (a daily budget), never a red zone. */
  threshold?: { value: number; label: string };
  /** Plot height: 'md' 160 px (default) or 'lg' 224 px. */
  size?: 'md' | 'lg';
  /** Accessible summary: "$331 over 30 days; the most was $38 on Oct 17". */
  label: string;
  className?: string;
}

const PLOT_H = { md: 'h-40', lg: 'h-56' } as const;

/**
 * One value per column, from zero (data-viz.md: bars start at zero, at most 5 ticks, dashed
 * horizontal guides only). Columns grow from the baseline once; later values ease. Hover or
 * focus a column for its exact value.
 */
export function ColumnChart({ values, contextBefore = 0, slot = 1, columnLabel, columnNote, format, axisFormat = format, xLabels = [], threshold, size = 'md', label, className }: ColumnChartProps) {
  const top = niceCeil(Math.max(threshold?.value ?? 0, ...values) * 1.05);
  const ticks = [top, top / 2, 0];
  const n = values.length;
  return (
    <figure className={cn('flex min-w-0 flex-col gap-2', className)}>
      <div className="flex min-w-0 gap-3">
        <div aria-hidden className={cn('flex shrink-0 flex-col justify-between text-right text-caption text-fg-subtle num', PLOT_H[size])}>
          {ticks.map((t) => (
            <span key={t} className="-my-2 leading-4">
              {axisFormat(t)}
            </span>
          ))}
        </div>
        <div role="img" aria-label={label} className={cn('relative flex min-w-0 flex-1 items-end gap-0.5', PLOT_H[size])}>
          <span aria-hidden className="pointer-events-none absolute inset-x-0 top-0 border-t border-dashed border-border-subtle" />
          <span aria-hidden className="pointer-events-none absolute inset-x-0 top-1/2 border-t border-dashed border-border-subtle" />
          <span aria-hidden className="pointer-events-none absolute inset-x-0 bottom-0 border-t border-border-subtle" />
          {values.map((v, i) => (
            <Tooltip
              key={i}
              side="top"
              content={
                <span className="flex flex-col gap-0.5">
                  <span className="font-medium">{columnLabel(i)}</span>
                  <span className="text-fg num">{format(v)}</span>
                  {columnNote?.(i)}
                </span>
              }
            >
              <span tabIndex={-1} className="group/col relative flex h-full min-w-0 flex-1 flex-col justify-end rounded-control transition-colors duration-(--duration-fast) hover:bg-hover">
                {v > 0 ? (
                  <span
                    aria-hidden
                    className={cn('motion-grow w-full rounded-t-control opacity-90 group-hover/col:opacity-100', i < contextBefore ? 'bg-fg-faint' : DATA_SLOT_BG[slot])}
                    style={{ height: `${Math.max(2, (v / top) * 100)}%`, transitionDelay: `${i * 12}ms` }}
                  />
                ) : (
                  <span aria-hidden className="h-0.5 w-full rounded-pill bg-border" />
                )}
              </span>
            </Tooltip>
          ))}
          {/* After the columns, so the line and its label draw over them. */}
          {threshold && threshold.value <= top && (
            <span aria-hidden className="pointer-events-none absolute inset-x-0 border-t border-dashed border-border-strong" style={{ bottom: `${(threshold.value / top) * 100}%` }}>
              <span className="absolute right-0 bottom-1 rounded-control bg-surface px-1 text-caption text-fg-muted">{threshold.label}</span>
            </span>
          )}
        </div>
      </div>
      {xLabels.length > 0 && (
        <figcaption aria-hidden className="flex min-w-0 gap-3 text-caption text-fg-subtle">
          {/* Holds the value axis's width, so each label sits under its column. */}
          <span className="invisible shrink-0 num">{axisFormat(top)}</span>
          <span className="relative h-4 min-w-0 flex-1">
            {xLabels.map((x) => (
              <span key={x.index} className="absolute -translate-x-1/2 whitespace-nowrap" style={{ left: `${((x.index + 0.5) / Math.max(1, n)) * 100}%` }}>
                {x.label}
              </span>
            ))}
          </span>
        </figcaption>
      )}
    </figure>
  );
}
